/**
 * MenuBar — the classic 11-menu bar (Faz 3.6): File / Edit / View / Insert /
 * Format / Tools / Draw / Dimension / Modify / Parametric / Help. Chakra
 * Menu row; every entry runs a registered command or shell action (the
 * compile-time RegisteredCommandId check guarantees no dead entries — the
 * static twin of OCS's every_menu_entry_runs_a_known_command test).
 * Visibility: useViewStore.menubarVisible (MENUBAR 0/1).
 */

import { Box, Flex, Menu, MenuButton, MenuItem, MenuList, Text } from '@chakra-ui/react';
import { useMemo } from 'react';
import { MENU_BAR, type MenuEntryDef } from '../../../config/ribbonTabs';
import { runRibbonBinding, primaryCommandName } from './ribbonCommand';
import { useTKey } from './KeytipBadge';
import { useCommandManager } from '../../../state/commandStore';

function MenuRow({ entry }: { entry: MenuEntryDef }) {
    const t = useTKey();
    const commands = useCommandManager((s) => s.commands);

    // Shortcut hint: registrar-assigned shortcut for the canonical id, else
    // the static hint from the menu table (topbar defaults).
    const registrarId = entry.cmdId ? `cmd.${entry.cmdId}` : null;
    const dynamic = registrarId ? commands[registrarId]?.shortcut : undefined;
    const hint = dynamic || entry.shortcut || (entry.cmdId ? primaryCommandName(entry.cmdId) : '');

    return (
        <MenuItem
            fontSize="12px"
            color="text.primary"
            justifyContent="space-between"
            onClick={() => runRibbonBinding(entry)}
            _hover={{ bg: 'bg.hover' }}
        >
            <Text as="span">{t(`ribbon.menu.entries.${entry.key}`)}</Text>
            {hint && (
                <Text as="span" fontSize="10px" color="text.secondary" ml={4} whiteSpace="nowrap">
                    {hint}
                </Text>
            )}
        </MenuItem>
    );
}

export function MenuBar() {
    const t = useTKey();

    // Menus with divider indexes resolved into render groups.
    const menus = useMemo(
        () => MENU_BAR.map((menu) => ({ menu })),
        []
    );

    return (
        <Flex
            h="24px"
            align="stretch"
            px={2}
            gap={1}
            bg="bg.toolbar"
            borderBottom="1px solid"
            borderColor="border.base"
            flexShrink={0}
            position="relative"
            zIndex={0}
        >
            {menus.map(({ menu }) => (
                <Menu key={menu.key} closeOnSelect autoSelect={false} placement="bottom-start">
                    <MenuButton
                        as={Box}
                        role="button"
                        display="flex"
                        alignItems="center"
                        px={2}
                        fontSize="12px"
                        color="text.primary"
                        whiteSpace="nowrap"
                        flexShrink={0}
                        borderRadius="3px"
                        _hover={{ bg: 'bg.hover' }}
                        _expanded={{ bg: 'bg.active' }}
                        _focus={{ outline: 'none' }}
                    >
                        {t(`ribbon.menu.${menu.key}`)}
                    </MenuButton>
                    <MenuList w="max-content" minW="220px" bg="bg.panel" borderColor="border.base">
                        {menu.entries.map((entry, idx) => (
                            <Box key={entry.key}>
                                <MenuRow entry={entry} />
                                {menu.dividers?.includes(idx) && (
                                    <Box as="hr" my={1} border="0" h="1px" bg="border.base" />
                                )}
                            </Box>
                        ))}
                    </MenuList>
                </Menu>
            ))}
        </Flex>
    );
}
